import { Box, Card, Table, Thead, Tbody, Tr, Th, Td, Text } from "@chakra-ui/react";
import { kpiSections } from "./kpiData";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import Axios from "../utils/httpClient";

const PRIMARY = "#133654";

const Head = ({ showApplicationCount, showUserPoints }) => (
  <Tr>
    <Th color={PRIMARY} minW="40px">Kod</Th>
    <Th color={PRIMARY} minW={{ base: "200px", md: "auto" }}>Parametr</Th>
    {showUserPoints && <Th color={PRIMARY} minW="50px">Ball</Th>}
    {showApplicationCount && (
      <>
        <Th color={PRIMARY} textAlign="center" minW="60px">
          <span className="desktop-text">Yangi</span>
          <span className="mobile-text">Y</span>
        </Th>
        <Th color={PRIMARY} textAlign="center" minW="60px">
          <span className="desktop-text">Baholangan</span>
          <span className="mobile-text">B</span>
        </Th>
        <Th color={PRIMARY} textAlign="center" minW="70px">
          <span className="desktop-text">Baholanmagan</span>
          <span className="mobile-text">BM</span>
        </Th>
      </>
    )}
  </Tr>
);

const SectionHeader = ({ title, color, showApplicationCount, showUserPoints, categoryKey, onCategoryClick, isClickable, collectedPoints, maxPoints }) => {
  // Remove existing max points from title (e.g., "– 35") if we're showing collected/max format
  const displayTitle = showUserPoints && collectedPoints !== undefined && maxPoints !== undefined
    ? title.replace(/\s*[–-]\s*\d+\s*$/, '') // Remove trailing "– 35" or "- 35"
    : title;

  return (
    <Tr>
      <Td
        colSpan={(showApplicationCount ? 5 : 2) + (showUserPoints ? 1 : 0)}
        textAlign="center"
        py={4}
        bg="white"
        color="black"
        fontWeight="bold"
        fontSize="lg"
        borderTop={`2px solid ${color}`}
        cursor={isClickable ? "pointer" : "default"}
        _hover={isClickable ? { bg: "gray.50", textDecoration: "underline" } : {}}
        onClick={isClickable ? () => onCategoryClick(categoryKey) : undefined}
      >
        {displayTitle}
        {showUserPoints && collectedPoints !== undefined && maxPoints !== undefined && (
          <span style={{ marginLeft: '8px' }}>– {collectedPoints}/{maxPoints}</span>
        )}
      </Td>
    </Tr>
  );
};

const getRowBg = (code, isDepartmentHead = false, isClickable = false) => {
  const [groupStr, itemStr] = String(code).split(".");
  const group = Number(groupStr);
  const item = Number(itemStr);

  if (isDepartmentHead) {
    // For department heads, only color rows that are actually clickable for their department
    return isClickable ? "rgba(46, 204, 113, 0.15)" : "white";
  }

  // Regular user view - use normal group colors
  if (group === 1 && item >= 1 && item <= 9) return "rgba(30, 144, 255, 0.2)";
  if (group === 2 && item >= 1 && item <= 12) return "rgba(46, 204, 113, 0.2)";
  if (group === 3 && item >= 1 && item <= 4) return "rgba(241, 196, 15, 0.2)";
  if (group === 4 && item >= 1 && item <= 7) return "rgba(231, 76, 60, 0.2)";
  return undefined;
};

const KpiMiniTable = ({ isDepartmentHead = false, fromWelcome = false }) => {
  const navigate = useNavigate();
  const { user } = useSelector(s => s);
  const [applicationCounts, setApplicationCounts] = useState({});
  const [evaluatedCounts, setEvaluatedCounts] = useState({});
  const [unevaluatedCounts, setUnevaluatedCounts] = useState({});
  const [userPoints, setUserPoints] = useState({});
  const [loading, setLoading] = useState(false);

  const isParameterClickable = (code) => {
    if (!isDepartmentHead || user?.role === 3) {
      return true;
    }

    const paramCode = String(code);

    const departmentName = user?.partition?.name?.toLowerCase() || "";

    if (departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati")) {
      return ["1.1", "1.2", "1.5"].includes(paramCode);
    }

    if (departmentName.includes("o'quv") || departmentName.includes("oquv")) {
      // Updated to include 1.1, 1.2, 1.3, 1.4, 3.2, 3.3 for O'quv bo'limi
      return ["1.1", "1.2", "1.3", "1.4", "1.6", "1.7", "1.8", "1.9", "3.2", "3.3"].includes(paramCode);
    }

    if (departmentName.includes("ilmiy bo'lim")) {
      // Include all 2.x parameters and 3.1, 3.2, 3.3, 3.4 for Ilmiy bo'lim
      return paramCode.startsWith("2.") || ["3.1", "3.2", "3.3", "3.4"].includes(paramCode);
    }

    if (departmentName.includes("xalqaro hamkorlik")) {
      // Include 3.1, 3.2, 3.3, 3.4 for Xalqaro hamkorlik bo'limi
      return ["3.1", "3.2", "3.3", "3.4"].includes(paramCode);
    }

    if (departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat")) {
      // Updated to include 1.3 and 1.4 for Ma'naviyat bo'limi as requested
      return ["1.3", "1.4", ...Array.from({ length: 7 }, (_, i) => `4.${i + 1}`)].includes(paramCode);
    }

    if (departmentName.includes("tarmoq texnologiyalari")) {
      return ["1.1", "1.2"].includes(paramCode); // Enable 1.1 and 1.2 for TT
    }

    return false;
  };

  // Function to load application counts
  const loadApplicationCounts = () => {
    if (isDepartmentHead && (user?.partition?.id || user?.role === 3)) {
      setLoading(true);

      // Fetch both pending applications (check-list) and all applications (list) to get complete counts
      // Use all_time=true to get all applications, not just current month
      const checkListPromise = Axios().get(`api/v1/application/check-list/?all_time=true`);
      const allApplicationsPromise = Axios().get(`api/v1/application/list/?all_time=true`);

      Promise.all([checkListPromise, allApplicationsPromise])
        .then(([checkListRes, allApplicationsRes]) => {
          const pendingApplications = checkListRes?.data?.applications || [];
          const allApplications = allApplicationsRes?.data?.applications || [];

          const newCounts = {};
          const evaluatedCountsMap = {};
          const unevaluatedCountsMap = {};

          const departmentName = user?.partition?.name?.toLowerCase() || "";
          const isTT = departmentName.includes("tarmoq texnologiyalari");
          const isTS = departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati");
          const isOV = departmentName.includes("o'quv") || departmentName.includes("oquv");
          const isMN = departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat");
          const isXL = departmentName.includes("xalqaro hamkorlik");
          const isIL = departmentName.includes("ilmiy bo'lim");
          const isMK = departmentName.includes("marketing");

          // Helper function to check if this department should count this application
          const shouldCountForDepartment = (app, code) => {
            if (user?.role === 3) return true; // Pro Rector sees everything
            // For KPI 1.1 (multi-department: TT, TS, OV)
            if (code === '1.1') {
              return (isTT || isTS || isOV);
            }
            // For KPI 1.3, 1.4 (multi-department: MN, OV)
            else if (code === '1.3' || code === '1.4') {
              return (isMN || isOV);
            }
            // For KPI 3.2, 3.3 (multi-department: OV, XL, IL)
            else if (code === '3.2' || code === '3.3') {
              return (isOV || isXL || isIL);
            }
            // For KPI 3.4 (multi-department: XL, IL)
            else if (code === '3.4') {
              return (isXL || isIL);
            }
            // For KPI 3.1, 2.1 (multi-department: XL, IL)
            else if (code === '3.1' || code === '2.1') {
              return (isXL || isIL);
            }
            // For single-department KPIs, check if the parameter belongs to this department
            else {
              // 1.6-1.9: O'quv bo'limi
              if (code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') {
                return isOV;
              }
              // 2.x (except 2.1, 2.2): Ilmiy bo'lim only
              if (code && code.startsWith('2.') && code !== '2.1' && code !== '2.2') {
                return isIL;
              }
              // 4.x: Ma'naviyat bo'limi
              if (code && code.startsWith('4.')) {
                return isMN;
              }
              // Fallback: check partition (for old applications that still have partition set)
              return app.partition_id === user.partition.id;
            }
          };

          // Helper function to check if application has been evaluated by this department
          const hasBeenEvaluatedByThisDept = (app, code) => {
            if (user?.role === 3) {
              return app.status === 4 || app.status === 5 || (app.ball && parseFloat(app.ball) > 0);
            }
            // Check department-specific evaluation status
            if (code === '1.1') {
              if (isTT) return app.tt_status === 1 || app.tt_status === 2;
              if (isTS) return app.ts_status === 1 || app.ts_status === 2;
              if (isOV) return app.ov_status === 1 || app.ov_status === 2;
            } else if (code === '1.3' || code === '1.4') {
              if (isMN) return app.mn_status === 1 || app.mn_status === 2;
              if (isOV) return app.ov_status === 1 || app.ov_status === 2;
            } else if (code === '3.2' || code === '3.3') {
              if (isOV) return app.ov_status === 1 || app.ov_status === 2;
              if (isXL) return app.tt_status === 1 || app.tt_status === 2;
              if (isIL) return app.ts_status === 1 || app.ts_status === 2;
            } else if (code === '3.4') {
              if (isXL) return app.tt_status === 1 || app.tt_status === 2;
              if (isIL) return app.ts_status === 1 || app.ts_status === 2;
            } else if (code === '3.1' || code === '2.1') {
              if (isXL) return app.tt_status === 1 || app.tt_status === 2;
              if (isIL) return app.ts_status === 1 || app.ts_status === 2;
            } else if (code.startsWith('2.')) {
              // All 2.x parameters use Ilmiy (ts_status)
              if (isIL) return app.ts_status === 1 || app.ts_status === 2;
            } else {
              // For single-department KPIs, use global status or points
              return app.status === 4 || app.status === 5 || (app.ball && parseFloat(app.ball) > 0);
            }
            return false;
          };

          // Process pending applications for "Yangi" and "Baholanmagan" counts
          pendingApplications.forEach(app => {
            const code = app?.parameter?.code;
            if (!code || !shouldCountForDepartment(app, code)) return;

            // Check if this application has been evaluated by this department
            const isEvaluatedByThisDept = hasBeenEvaluatedByThisDept(app, code);

            // Skip applications that have already been evaluated by this department
            if (isEvaluatedByThisDept) {
              return;
            }

            // Check if THIS department has seen it
            let isNewForThisDept = false;

            if (code === '1.1') {
              if (isTT) isNewForThisDept = app.tt_status === 0;
              else if (isTS) isNewForThisDept = app.ts_status === 0;
              else if (isOV) isNewForThisDept = app.ov_status === 0;
            } else if (code === '1.3' || code === '1.4') {
              if (isMN) isNewForThisDept = app.mn_status === 0;
              else if (isOV) isNewForThisDept = app.ov_status === 0;
            } else if (code === '3.2' || code === '3.3') {
              if (isOV) isNewForThisDept = app.ov_status === 0;
              else if (isXL) isNewForThisDept = app.tt_status === 0;
              else if (isIL) isNewForThisDept = app.ts_status === 0;
            } else if (code === '3.4') {
              if (isXL) isNewForThisDept = app.tt_status === 0;
              else if (isIL) isNewForThisDept = app.ts_status === 0;
            } else if (code === '3.1' || code === '2.1') {
              if (isXL) isNewForThisDept = app.tt_status === 0;
              else if (isIL) isNewForThisDept = app.ts_status === 0;
            } else if (code.startsWith('2.')) {
              // All 2.x parameters use Ilmiy (ts_status)
              if (isIL) isNewForThisDept = app.ts_status === 0;
            } else {
              // For single-department KPIs, check department-specific status
              if ((code === '1.2' || code === '1.5') && isTS) {
                isNewForThisDept = app.ts_status === 0;
              } else if ((code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') && isOV) {
                isNewForThisDept = app.ov_status === 0;
              } else if (code.startsWith('2.') && isIL) {
                isNewForThisDept = app.ts_status === 0; // IL uses ts_status
              } else if (code.startsWith('4.') && isMN) {
                isNewForThisDept = app.mn_status === 0;
              } else {
                // Fallback: use global status
                isNewForThisDept = app.status === 1;
              }
            }

            // Yangi arizalar: not yet seen by THIS department
            if (isNewForThisDept) {
              newCounts[code] = (newCounts[code] || 0) + 1;
            }

            // Baholanmagan: status = 1, 2, or 3 (not yet evaluated - includes new and seen)
            // Only count if not evaluated by this department
            if (app.status === 1 || app.status === 2 || app.status === 3) {
              unevaluatedCountsMap[code] = (unevaluatedCountsMap[code] || 0) + 1;
            }
          });

          // Process all applications for "Baholangan" count
          allApplications.forEach(app => {
            const code = app?.parameter?.code;
            if (!code || !shouldCountForDepartment(app, code)) return;

            // Count applications that have been evaluated by this department
            if (hasBeenEvaluatedByThisDept(app, code)) {
              evaluatedCountsMap[code] = (evaluatedCountsMap[code] || 0) + 1;

              // Debug: Log which applications are being counted as evaluated
              if (code === '1.1') {
                console.log(`Counting evaluated app ${app.id} for KPI 1.1:`, {
                  status: app.status,
                  ball: app.ball,
                  tt_status: app.tt_status,
                  ts_status: app.ts_status,
                  ov_status: app.ov_status,
                  user: app.user?.first_name,
                  hasPoints: app.ball && parseFloat(app.ball) > 0
                });
              }
            }
          });

          setApplicationCounts(newCounts);
          setEvaluatedCounts(evaluatedCountsMap);
          setUnevaluatedCounts(unevaluatedCountsMap);
        })
        .catch(e => {
          console.error('Error loading data:', e);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // Fetch application counts for each parameter
  useEffect(() => {
    loadApplicationCounts();
  }, [isDepartmentHead, user?.partition?.id]);

  // Reload data when window regains focus (user comes back from detail page)
  useEffect(() => {
    const handleFocus = () => {
      if (isDepartmentHead) {
        console.log('Window focused, reloading application counts...');
        loadApplicationCounts();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && isDepartmentHead) {
        console.log('Page became visible, reloading application counts...');
        loadApplicationCounts();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isDepartmentHead, user?.partition?.id]);

  // Fetch user's points for each parameter
  useEffect(() => {
    if (!isDepartmentHead && !fromWelcome && user?.id) {
      Axios().get(`api/v1/application/my-points-by-param/`)
        .then(res => {
          setUserPoints(res?.data?.points_by_parameter || {});
        })
        .catch(e => {
          console.error('Error loading user points:', e);
        });
    }
  }, [isDepartmentHead, fromWelcome, user?.id]);

  const groupedRows = kpiSections.reduce((acc, section) => {
    const groupNumber = section.key;
    if (!acc[groupNumber]) {
      acc[groupNumber] = {
        title: section.title,
        items: []
      };
    }
    acc[groupNumber].items.push(...section.items.map(i => ({
      id: i.no,
      no: i.no,
      parameter: i.parameter,
      maxBall: i.maxBall
    })));
    return acc;
  }, {});

  const sectionConfig = {
    "i": { color: "#1E90FF" },
    "ii": { color: "#2ECC71" },
    "iii": { color: "#F1C40F" },
    "iv": { color: "#E74C3C" }
  };

  const handleParameterClick = (code) => {
    if (isParameterClickable(code)) {
      // Add a query parameter to indicate this is from the welcome page
      const url = `/kpi/${code}${fromWelcome ? '?from=welcome' : ''}`;
      navigate(url);
    }
  };

  const handleViewEvaluatedTeachers = (code) => {
    navigate(`/teachers-with-points?parameter_code=${code}`);
  };

  return (
    <Card>
      <Box p={3} overflowX="auto" overflowY="visible">
        <Table size="sm" variant="simple" sx={{
          minWidth: { base: '600px', md: 'auto' },
          '@media (max-width: 768px)': {
            fontSize: '11px',
            '& th, & td': {
              padding: '6px 4px',
              fontSize: '11px',
              whiteSpace: 'nowrap'
            },
            '& th:first-of-type, & td:first-of-type': {
              minWidth: '40px'
            },
            '& th:nth-of-type(2), & td:nth-of-type(2)': {
              minWidth: '200px',
              maxWidth: '250px',
              whiteSpace: 'normal',
              wordBreak: 'break-word'
            }
          }
        }}>
          <Thead><Head showApplicationCount={isDepartmentHead} showUserPoints={!isDepartmentHead && !fromWelcome} /></Thead>
          <Tbody>
            {Object.entries(groupedRows).map(([groupNumber, groupData]) => {
              // Calculate category totals for teachers
              const categoryCollectedPoints = !isDepartmentHead && !fromWelcome
                ? parseFloat(groupData.items.reduce((sum, item) => sum + (parseFloat(userPoints[item.no]) || 0), 0).toFixed(2))
                : undefined;
              const categoryMaxPoints = !isDepartmentHead && !fromWelcome
                ? groupData.items.reduce((sum, item) => sum + (parseFloat(item.maxBall) || 0), 0)
                : undefined;

              return (
                <>
                  <SectionHeader
                    key={`header-${groupNumber}`}
                    title={groupData.title}
                    color={sectionConfig[groupNumber]?.color || "#666"}
                    showApplicationCount={isDepartmentHead}
                    showEvaluatedCount={true}
                    showUserPoints={!isDepartmentHead && !fromWelcome}
                    categoryKey={groupNumber}
                    onCategoryClick={(key) => navigate(`/teachers-by-category/${key}`)}
                    isClickable={fromWelcome}
                    collectedPoints={categoryCollectedPoints}
                    maxPoints={categoryMaxPoints}
                  />
                  {groupData.items.map(r => {
                    const clickable = isParameterClickable(r.no);
                    const newCount = applicationCounts[r.no] || 0;
                    const evaluatedCount = evaluatedCounts[r.no] || 0;
                    const unevaluatedCount = unevaluatedCounts[r.no] || 0;
                    return (
                      <Tr key={r.no} bg={getRowBg(r.no, isDepartmentHead, clickable)}>
                        <Td>
                          {r.no}
                        </Td>
                        <Td>
                          <Text
                            as={clickable ? "button" : "span"}
                            color={clickable ? "inherit" : "gray.500"}
                            textAlign="left"
                            _hover={clickable ? { textDecoration: "underline" } : {}}
                            onClick={clickable ? () => {
                              if (fromWelcome) {
                                // On welcome page, clicking parameter should show teachers with points in that range
                                handleViewEvaluatedTeachers(r.no);
                              } else {
                                // On other pages, clicking parameter should go to KPI detail
                                handleParameterClick(r.no);
                              }
                            } : undefined}
                            cursor={clickable ? "pointer" : "default"}
                            filter={clickable ? "none" : "grayscale(100%)"}
                          >
                            {r.parameter} - {r.maxBall}
                          </Text>
                        </Td>
                        {!isDepartmentHead && !fromWelcome && <Td>{userPoints[r.no] || 0}</Td>}
                        {isDepartmentHead && (
                          <>
                            <Td textAlign="center">
                              <Text
                                as={clickable && newCount > 0 ? "button" : "span"}
                                fontWeight={newCount > 0 ? "bold" : "normal"}
                                color={newCount > 0 ? "blue.600" : "gray.400"}
                                cursor={clickable && newCount > 0 ? "pointer" : "default"}
                                _hover={clickable && newCount > 0 ? { textDecoration: "underline" } : {}}
                                onClick={clickable && newCount > 0 ? () => navigate(`/kpi/${r.no}?filter=yangi`) : undefined}
                              >
                                {loading ? "..." : newCount}
                              </Text>
                            </Td>
                            <Td textAlign="center">
                              <Text
                                as={clickable && evaluatedCount > 0 ? "button" : "span"}
                                fontWeight={evaluatedCount > 0 ? "bold" : "normal"}
                                color={evaluatedCount > 0 ? "green.600" : "gray.400"}
                                cursor={clickable && evaluatedCount > 0 ? "pointer" : "default"}
                                _hover={clickable && evaluatedCount > 0 ? { textDecoration: "underline" } : {}}
                                onClick={clickable && evaluatedCount > 0 ? () => navigate(`/kpi/${r.no}?filter=baholanganlar`) : undefined}
                              >
                                {loading ? "..." : evaluatedCount}
                              </Text>
                            </Td>
                            <Td textAlign="center">
                              <Text
                                as={clickable && unevaluatedCount > 0 ? "button" : "span"}
                                fontWeight={unevaluatedCount > 0 ? "bold" : "normal"}
                                color={unevaluatedCount > 0 ? "orange.600" : "gray.400"}
                                cursor={clickable && unevaluatedCount > 0 ? "pointer" : "default"}
                                _hover={clickable && unevaluatedCount > 0 ? { textDecoration: "underline" } : {}}
                                onClick={clickable && unevaluatedCount > 0 ? () => navigate(`/kpi/${r.no}?filter=baholanmaganlar`) : undefined}
                              >
                                {loading ? "..." : unevaluatedCount}
                              </Text>
                            </Td>
                          </>
                        )}
                      </Tr>
                    );
                  })}
                </>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
};

export default KpiMiniTable;